/**
 * Pure calendar-grid helpers for the feriados configuration area (Unit S6,
 * design.md, spec `payroll-holidays-config`). No DOM, no I/O, no
 * `Date.now()` — same purity contract as `liquidacion-periodo-policy.js`,
 * but a DIFFERENT concern (a display grid over a month, not a settlement
 * period range), so it lives in its own module rather than growing that one
 * (mirrors AD2's own reasoning: a period-range module stays honest about
 * what it is).
 *
 * All arithmetic is `Date.UTC(year, month, day)` on parsed Y/M/D components
 * — never `new Date(string)` on a local clock — so the grid is identical
 * regardless of the host machine's timezone (same technique
 * `liquidacion-periodo-policy.js#mesARango` already uses).
 */

const YYYY_MM_RE = /^(\d{4})-(\d{2})$/;
const DAY_MS = 86400000;

/** @param {*} yyyyMm @returns {{year:number, month:number}|null} */
function parseYyyyMm(yyyyMm) {
    if (typeof yyyyMm !== 'string') return null;
    const m = YYYY_MM_RE.exec(yyyyMm);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]);
    if (month < 1 || month > 12) return null;
    return { year, month };
}

/** @param {number} n @returns {string} */
function pad2(n) {
    return String(n).padStart(2, '0');
}

/** @param {number} utcMs @returns {string} `'YYYY-MM-DD'` */
function formatFechaUtcMs(utcMs) {
    const d = new Date(utcMs);
    return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * Días del mes en una grilla de semanas, lunes primero (spec "a 7-column
 * calendar"). Cada celda es `{ fecha, dia, enMes }` — `fecha` SIEMPRE
 * poblada, incluye días de relleno del mes anterior/siguiente para
 * completar cada semana; `enMes` distingue cuáles pertenecen al mes pedido.
 * @param {string} yyyyMm `'YYYY-MM'`
 * @returns {Array<Array<{fecha:string,dia:number,enMes:boolean}>>|null} semanas, o `null` si `yyyyMm` es inválido
 */
export function calendarioDeMes(yyyyMm) {
    const parsed = parseYyyyMm(yyyyMm);
    if (!parsed) return null;
    const { year, month } = parsed;

    const firstOfMonthMs = Date.UTC(year, month - 1, 1);
    // getUTCDay(): 0=domingo..6=sábado -> desplazar para que la semana empiece lunes.
    const leadingOffset = (new Date(firstOfMonthMs).getUTCDay() + 6) % 7;
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const totalCells = Math.ceil((leadingOffset + daysInMonth) / 7) * 7;

    const celdas = [];
    for (let i = 0; i < totalCells; i += 1) {
        const offsetDias = i - leadingOffset;
        const cellMs = firstOfMonthMs + offsetDias * DAY_MS;
        celdas.push({
            fecha: formatFechaUtcMs(cellMs),
            dia: new Date(cellMs).getUTCDate(),
            enMes: offsetDias >= 0 && offsetDias < daysInMonth,
        });
    }

    const semanas = [];
    for (let i = 0; i < celdas.length; i += 7) semanas.push(celdas.slice(i, i + 7));
    return semanas;
}

/**
 * Feriados de un mes dado, fecha ascendente — filtro puro sobre la lista
 * completa ya cargada (la app no pagina feriados por mes;
 * `feriados.repository.js#listFeriados` trae todos).
 * @param {Array<{fecha:string,descripcion:string}>} feriados
 * @param {string} yyyyMm
 * @returns {Array<{fecha:string,descripcion:string}>}
 */
export function feriadosDelMes(feriados, yyyyMm) {
    if (!Array.isArray(feriados) || typeof yyyyMm !== 'string') return [];
    return feriados
        .filter((f) => typeof f?.fecha === 'string' && f.fecha.startsWith(`${yyyyMm}-`))
        .sort((a, b) => (a.fecha < b.fecha ? -1 : a.fecha > b.fecha ? 1 : 0));
}

/**
 * Mes siguiente en formato `'YYYY-MM'`, para el nav "‹ ›" del calendario.
 * `null` en input inválido — nunca desliza a un mes "parecido".
 * @param {string} yyyyMm
 * @returns {string|null}
 */
export function mesSiguiente(yyyyMm) {
    const parsed = parseYyyyMm(yyyyMm);
    if (!parsed) return null;
    const { year, month } = parsed;
    return month === 12 ? `${year + 1}-01` : `${year}-${pad2(month + 1)}`;
}

/**
 * Mes anterior en formato `'YYYY-MM'` — ver {@link mesSiguiente}.
 * @param {string} yyyyMm
 * @returns {string|null}
 */
export function mesAnterior(yyyyMm) {
    const parsed = parseYyyyMm(yyyyMm);
    if (!parsed) return null;
    const { year, month } = parsed;
    return month === 1 ? `${year - 1}-12` : `${year}-${pad2(month - 1)}`;
}
